import { useMemo } from 'react';
import { Clapperboard, OctagonX, RotateCcw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { buildGifPlan } from '@/services/gifPlan';
import { useProjectStore } from '@/store/projectStore';
import { formatBytes, formatSecondsHuman } from '@/utils/format';
import { cancelConversion, retryConversion, startConversion } from './convertActions';

const PHASE_LABELS: Record<string, string> = {
  'loading-engine': 'Preparing the encoder…',
  palette: 'Pass 1 · building the color palette',
  encoding: 'Pass 2 · encoding frames',
};

export function ConversionPanel() {
  const meta = useProjectStore((state) => state.meta);
  const trim = useProjectStore((state) => state.trim);
  const crop = useProjectStore((state) => state.crop);
  const settings = useProjectStore((state) => state.settings);
  const conversion = useProjectStore((state) => state.conversion);

  const plan = useMemo(
    () => (meta ? buildGifPlan(meta, trim, crop, settings) : null),
    [meta, trim, crop, settings],
  );

  if (!meta || !plan) return null;

  const { phase, progress, etaSeconds, error } = conversion;
  const running = phase === 'loading-engine' || phase === 'palette' || phase === 'encoding';
  const percent = Math.round(progress * 100);

  return (
    <section aria-label="Convert" className="panel space-y-4 p-4">
      <div>
        <h3 className="eyebrow">Planned output</h3>
        <p className="mt-1.5 font-mono text-sm tabular-nums">
          {plan.width}×{plan.height} · {plan.fps} fps · {plan.outputDuration.toFixed(1)} s
        </p>
        <p className="font-mono text-xs text-ink-500 tabular-nums dark:text-ink-400">
          {plan.frameCount.toLocaleString()} frames · ≈ {formatBytes(plan.estimatedBytes)} ·{' '}
          {plan.maxColors} colors
        </p>
        {plan.warnings[0] && (
          <p className="mt-2 text-xs text-signal-700 dark:text-signal-400">{plan.warnings[0]}</p>
        )}
      </div>

      {running ? (
        <div className="space-y-3" aria-live="polite">
          <div className="flex items-baseline justify-between gap-2">
            <p className="text-sm font-medium">{PHASE_LABELS[phase]}</p>
            <p className="font-mono text-xs text-ink-500 tabular-nums dark:text-ink-400">
              {percent}%
            </p>
          </div>
          <div
            role="progressbar"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={percent}
            aria-label="Conversion progress"
            className="h-2 overflow-hidden rounded-full bg-ink-200 dark:bg-ink-700"
          >
            <div
              className="progress-scan h-full rounded-full bg-signal-500 transition-[width] duration-200"
              style={{ width: `${Math.max(2, percent)}%` }}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <p className="font-mono text-xs text-ink-500 tabular-nums dark:text-ink-400">
              {etaSeconds !== null ? `~${formatSecondsHuman(etaSeconds)} left` : 'estimating…'}
            </p>
            <Button variant="ghost" size="sm" onClick={cancelConversion}>
              <OctagonX aria-hidden className="size-4" />
              Cancel
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          {phase === 'error' && error && (
            <div
              role="alert"
              className="max-h-28 overflow-auto rounded-lg bg-bad-500/10 p-2.5 ring-1 ring-bad-500/30"
            >
              <p className="font-mono text-xs break-words whitespace-pre-wrap text-bad-600 dark:text-bad-400">
                {error}
              </p>
            </div>
          )}
          {phase === 'cancelled' && (
            <p className="text-xs text-ink-500 dark:text-ink-400">
              Cancelled — your edit and settings are untouched.
            </p>
          )}
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            onClick={() => void (phase === 'error' ? retryConversion() : startConversion())}
          >
            {phase === 'error' ? (
              <>
                <RotateCcw aria-hidden className="size-4" /> Try again
              </>
            ) : (
              <>
                <Clapperboard aria-hidden className="size-4" />
                {phase === 'done' ? 'Convert again' : 'Convert to GIF'}
              </>
            )}
          </Button>
        </div>
      )}
    </section>
  );
}
